/**
 * Enhanced Error Boundary Component
 * Comprehensive error handling with context-aware recovery strategies
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertTriangle,
  RefreshCw,
  Home,
  Bug,
  Copy,
  ExternalLink,
  Shield,
  ArrowLeft,
  Info,
  Zap,
  Activity,
  CheckCircle,
  XCircle,
  FileText,
  Send,
  Download
} from 'lucide-react';

// Types
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorContext: ErrorContext;
  retryCount: number;
  isReporting: boolean;
  reportSent: boolean;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  context?: string;
  enableRetry?: boolean;
  enableReporting?: boolean;
  maxRetries?: number;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorContext {
  timestamp: string;
  userAgent: string;
  url: string;
  userId?: string;
  sessionId?: string;
  buildVersion?: string;
  context?: string;
  componentStack: string;
}

interface ErrorReport {
  error: {
    name: string;
    message: string;
    stack?: string;
    cause?: any;
  };
  context: ErrorContext;
  recovery: {
    retryCount: number;
    recoveryAttempts: string[];
  };
  userFeedback?: string;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private retryTimeout: NodeJS.Timeout | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorContext: this.createErrorContext(),
      retryCount: 0,
      isReporting: false,
      reportSent: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorContext = this.createErrorContext(errorInfo);
    
    this.setState({
      error,
      errorInfo,
      errorContext,
    });

    // Call external error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log to console for development
    console.group('🚨 Error Boundary Caught Error');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Context:', errorContext);
    console.groupEnd();

    // Send to error tracking service
    this.logErrorToService(error, errorInfo, errorContext);
  }

  private createErrorContext(errorInfo?: ErrorInfo): ErrorContext {
    return {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getUserId(),
      sessionId: this.getSessionId(),
      buildVersion: import.meta.env?.VITE_APP_VERSION || 'unknown',
      context: this.props.context || 'unknown',
      componentStack: errorInfo?.componentStack || 'No component stack available'
    };
  }

  private getUserId(): string | undefined {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.id;
    } catch {
      return undefined;
    }
  }

  private getSessionId(): string | undefined {
    return sessionStorage.getItem('sessionId') || undefined;
  }

  private async logErrorToService(error: Error, errorInfo: ErrorInfo, context: ErrorContext) {
    try {
      const errorReport: ErrorReport = {
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
          cause: (error as any).cause,
        },
        context,
        recovery: {
          retryCount: this.state.retryCount,
          recoveryAttempts: [],
        },
      };

      // Send to error tracking service (e.g., Sentry, LogRocket, etc.)
      await fetch('/api/v1/errors/report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorReport),
      });
    } catch (reportError) {
      console.error('Failed to report error:', reportError);
    }
  }

  private handleRetry = () => {
    const { maxRetries = 3 } = this.props;
    
    if (this.state.retryCount >= maxRetries) {
      return;
    }

    this.setState((prevState) => ({
      retryCount: prevState.retryCount + 1,
    }));

    // Add a delay before retry to prevent immediate re-error
    this.retryTimeout = setTimeout(() => {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
      });
    }, 1000);
  };

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isReporting: false,
      reportSent: false,
    });
  };

  private handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  private handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      this.handleGoHome();
    }
  };

  private copyErrorToClipboard = () => {
    const { error, errorInfo, errorContext } = this.state;
    
    const errorText = `
Error: ${error?.name}: ${error?.message}

Stack Trace:
${error?.stack}

Component Stack:
${errorInfo?.componentStack}

Context:
${JSON.stringify(errorContext, null, 2)}

User Agent: ${navigator.userAgent}
URL: ${window.location.href}
Timestamp: ${new Date().toISOString()}
    `.trim();

    navigator.clipboard.writeText(errorText);
  };

  private downloadErrorReport = () => {
    const { error, errorInfo, errorContext } = this.state;
    
    const errorReport = {
      error: {
        name: error?.name,
        message: error?.message,
        stack: error?.stack,
      },
      errorInfo,
      context: errorContext,
      timestamp: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(errorReport, null, 2)], {
      type: 'application/json',
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `error-report-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  private sendFeedback = async (feedback: string) => {
    if (!feedback.trim()) return;

    this.setState({ isReporting: true });

    try {
      await fetch('/api/v1/errors/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          error: {
            name: this.state.error?.name,
            message: this.state.error?.message,
          },
          context: this.state.errorContext,
          feedback: feedback.trim(),
        }),
      });

      this.setState({ reportSent: true });
    } catch (error) {
      console.error('Failed to send feedback:', error);
    } finally {
      this.setState({ isReporting: false });
    }
  };

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Use custom fallback if provided
    if (this.props.fallback) {
      return this.props.fallback;
    }

    const { error, errorContext, retryCount, isReporting, reportSent } = this.state;
    const { enableRetry = true, enableReporting = true, maxRetries = 3 } = this.props;

    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center p-4">
        <Card className="w-full max-w-4xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            <CardTitle className="text-2xl">Something went wrong</CardTitle>
            <CardDescription className="text-lg">
              We encountered an unexpected error. Our team has been notified.
            </CardDescription>
          </CardHeader>

          <CardContent>
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="actions">Actions</TabsTrigger>
                <TabsTrigger value="feedback">Feedback</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Bug className="w-5 h-5 text-destructive" />
                        <span className="font-medium">Error Information</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Type:</span>
                          <span className="ml-2 font-medium">{error?.name || 'Unknown Error'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Context:</span>
                          <span className="ml-2">{errorContext.context}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Time:</span>
                          <span className="ml-2">{new Date(errorContext.timestamp).toLocaleString()}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Activity className="w-5 h-5 text-blue-500" />
                        <span className="font-medium">Recovery Status</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Retry attempts:</span>
                          <Badge variant={retryCount >= maxRetries ? 'destructive' : 'secondary'}>
                            {retryCount}/{maxRetries}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Auto-recovery:</span>
                          {retryCount < maxRetries ? (
                            <Badge variant="default" className="text-green-800 bg-green-100">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Available
                            </Badge>
                          ) : (
                            <Badge variant="destructive">
                              <XCircle className="w-3 h-3 mr-1" />
                              Exhausted
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Error Message */}
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="font-medium">
                    {error?.message || 'An unexpected error occurred while processing your request.'}
                  </AlertDescription>
                </Alert>

                {/* Quick Actions */}
                <div className="flex flex-col sm:flex-row gap-3">
                  {enableRetry && retryCount < maxRetries && (
                    <Button onClick={this.handleRetry} className="flex-1">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Try Again ({maxRetries - retryCount} attempts left)
                    </Button>
                  )}
                  <Button variant="outline" onClick={this.handleGoBack} className="flex-1">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Go Back
                  </Button>
                  <Button variant="outline" onClick={this.handleGoHome} className="flex-1">
                    <Home className="w-4 h-4 mr-2" />
                    Home
                  </Button>
                </div>
              </TabsContent>

              {/* Details Tab */}
              <TabsContent value="details" className="space-y-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Error Message
                    </h4>
                    <div className="p-3 bg-muted rounded-lg font-mono text-sm">
                      {error?.message || 'No error message available'}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Stack Trace</h4>
                    <ScrollArea className="h-32 p-3 bg-muted rounded-lg">
                      <pre className="text-xs font-mono whitespace-pre-wrap">
                        {error?.stack || 'No stack trace available'}
                      </pre>
                    </ScrollArea>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Component Stack</h4>
                    <ScrollArea className="h-24 p-3 bg-muted rounded-lg">
                      <pre className="text-xs font-mono whitespace-pre-wrap">
                        {this.state.errorInfo?.componentStack || 'No component stack available'}
                      </pre>
                    </ScrollArea>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Environment Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div className="p-3 bg-muted rounded-lg">
                        <div className="font-medium mb-1">Browser</div>
                        <div className="text-muted-foreground font-mono text-xs break-all">
                          {errorContext.userAgent}
                        </div>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <div className="font-medium mb-1">URL</div>
                        <div className="text-muted-foreground font-mono text-xs break-all">
                          {errorContext.url}
                        </div>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <div className="font-medium mb-1">Build Version</div>
                        <div className="text-muted-foreground">{errorContext.buildVersion}</div>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <div className="font-medium mb-1">Session ID</div>
                        <div className="text-muted-foreground font-mono">{errorContext.sessionId || 'N/A'}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Actions Tab */}
              <TabsContent value="actions" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Zap className="w-5 h-5" />
                        Recovery Actions
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {enableRetry && retryCount < maxRetries && (
                        <Button onClick={this.handleRetry} className="w-full">
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Retry Operation
                        </Button>
                      )}
                      <Button variant="outline" onClick={this.handleReset} className="w-full">
                        <Shield className="w-4 h-4 mr-2" />
                        Reset Component
                      </Button>
                      <Button variant="outline" onClick={this.handleGoBack} className="w-full">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Go Back
                      </Button>
                      <Button variant="outline" onClick={this.handleGoHome} className="w-full">
                        <Home className="w-4 h-4 mr-2" />
                        Return Home
                      </Button>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Debug Actions
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <Button variant="outline" onClick={this.copyErrorToClipboard} className="w-full">
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Error Details
                      </Button>
                      <Button variant="outline" onClick={this.downloadErrorReport} className="w-full">
                        <Download className="w-4 h-4 mr-2" />
                        Download Report
                      </Button>
                      <Button variant="outline" asChild className="w-full">
                        <a href="https://github.com/yourusername/yourrepo/issues" target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Report on GitHub
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Feedback Tab */}
              <TabsContent value="feedback" className="space-y-4">
                {enableReporting && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Send className="w-5 h-5" />
                        Help Us Improve
                      </CardTitle>
                      <CardDescription>
                        Tell us what you were trying to do when this error occurred. This helps us fix the issue faster.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {reportSent ? (
                        <div className="text-center py-6">
                          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                          <h3 className="text-lg font-semibold mb-2">Thank you for your feedback!</h3>
                          <p className="text-muted-foreground">
                            Your report has been sent to our development team. We'll use this information to improve the application.
                          </p>
                        </div>
                      ) : (
                        <FeedbackForm
                          onSubmit={this.sendFeedback}
                          isSubmitting={isReporting}
                        />
                      )}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    );
  }
}

// Feedback Form Component
interface FeedbackFormProps {
  onSubmit: (feedback: string) => void;
  isSubmitting: boolean;
}

function FeedbackForm({ onSubmit, isSubmitting }: FeedbackFormProps) {
  const [feedback, setFeedback] = React.useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (feedback.trim()) {
      onSubmit(feedback);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="feedback" className="block text-sm font-medium mb-2">
          What were you trying to do? (Optional)
        </label>
        <textarea
          id="feedback"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Describe what you were doing when the error occurred..."
          className="w-full min-h-[100px] p-3 border rounded-lg resize-vertical"
          disabled={isSubmitting}
        />
      </div>
      <Button type="submit" disabled={isSubmitting || !feedback.trim()} className="w-full">
        {isSubmitting ? (
          <>
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            Send Feedback
          </>
        )}
      </Button>
    </form>
  );
}

export default ErrorBoundary;